import { useState, useEffect } from 'react';
import { Database, Download, Trash2, Plus, Clock, Settings, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Backup {
  id: number;
  filename: string;
  size_mb: string;
  status: string;
  backup_type: string;
  created_at: string;
}

interface BackupSettings {
  auto_backup_enabled: string;
  auto_backup_time: string;
  auto_backup_interval: string;
}

export function BackupManagement() {
  const { user } = useAuth();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [settings, setSettings] = useState<BackupSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [newSettings, setNewSettings] = useState({
    auto_backup_enabled: false,
    auto_backup_time: '02:00',
    auto_backup_interval: 'daily',
  });

  useEffect(() => {
    loadBackups();
    loadSettings();
  }, []);

  const loadBackups = async () => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/backups/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.success) {
        setBackups(data.data || []);
      }
    } catch (error) {
      console.error('Load backups error:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/backups/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.success) {
        setSettings(data.data);
        setNewSettings({
          auto_backup_enabled: data.data.auto_backup_enabled === 'true',
          auto_backup_time: data.data.auto_backup_time || '02:00',
          auto_backup_interval: data.data.auto_backup_interval || 'daily',
        });
      }
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/backups/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        loadBackups();
      } else {
        alert(data.message || 'Yedek alınamadı');
      }
    } catch (error) {
      alert('Yedek alınamadı');
    } finally {
      setCreating(false);
    }
  };

  const deleteBackup = async (id: number) => {
    if (!confirm('Bu yedek silinsin mi?')) return;
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/backups/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await res.json();
      if (data.success) {
        loadBackups();
      }
    } catch (error) {
      alert('Yedek silinemedi');
    }
  };

  const downloadBackup = (filename: string) => {
    // Doğrudan backups klasöründen indir
    const downloadUrl = `${window.location.origin}/backups/${filename}`;
    window.open(downloadUrl, '_blank');
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_CONFIG.BASE_URL}/backups/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(newSettings),
      });
      const data = await res.json();
      if (data.success) {
        setSettingsOpen(false);
        loadSettings();
      }
    } catch (error) {
      alert('Ayarlar kaydedilemedi');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 px-6 py-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-blue-100 text-sm">
                <Database className="w-4 h-4" />
                <span>Yedekleme Yönetimi</span>
              </div>
              <h1 className="mt-2 text-2xl font-bold">Veritabanı Yedekleri</h1>
              <p className="mt-1 text-sm text-slate-200">
                Veritabanı yedeklerini alın, yönetin ve geri yükleyin
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSettingsOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20"
              >
                <Settings className="w-4 h-4" />
                Ayarlar
              </button>
              <button
                onClick={createBackup}
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Yeni Yedek
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
            <Database className="w-4 h-4" />
            <span>Toplam Yedek</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{backups.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
            <Clock className="w-4 h-4" />
            <span>Son Yedek</span>
          </div>
          <div className="text-lg font-bold text-slate-900">
            {backups[0] ? new Date(backups[0].created_at).toLocaleDateString('tr-TR') : '-'}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
            {settings?.auto_backup_enabled === 'true' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <span>Otomatik Yedek</span>
          </div>
          <div className="text-lg font-bold text-slate-900">
            {settings?.auto_backup_enabled === 'true' ? 'Aktif' : 'Pasif'}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Dosya</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Boyut</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Tür</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Durum</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-600">Tarih</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-slate-600">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {backups.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                  Henüz yedek bulunmuyor
                </td>
              </tr>
            ) : (
              backups.map((backup) => (
                <tr key={backup.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-slate-900">{backup.filename}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{backup.size_mb} MB</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        backup.backup_type === 'auto'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {backup.backup_type === 'auto' ? 'Otomatik' : 'Manuel'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        backup.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {backup.status === 'completed' ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                      {backup.status === 'completed' ? 'Tamamlandı' : 'Bekliyor'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {new Date(backup.created_at).toLocaleString('tr-TR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => downloadBackup(backup.filename)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="İndir"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBackup(backup.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {settingsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Otomatik Yedekleme Ayarları</h2>
              <button onClick={() => setSettingsOpen(false)} className="text-slate-400 hover:text-slate-600">
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="font-medium">Otomatik Yedekleme</label>
                <button
                  onClick={() =>
                    setNewSettings({ ...newSettings, auto_backup_enabled: !newSettings.auto_backup_enabled })
                  }
                  className={`w-12 h-6 rounded-full transition-colors ${
                    newSettings.auto_backup_enabled ? 'bg-blue-600' : 'bg-slate-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                      newSettings.auto_backup_enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Yedek Saati</label>
                <input
                  type="time"
                  value={newSettings.auto_backup_time}
                  onChange={(e) =>
                    setNewSettings({ ...newSettings, auto_backup_time: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Aralık</label>
                <select
                  value={newSettings.auto_backup_interval}
                  onChange={(e) =>
                    setNewSettings({ ...newSettings, auto_backup_interval: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg"
                >
                  <option value="daily">Her Gün</option>
                  <option value="weekly">Her Hafta</option>
                  <option value="monthly">Her Ay</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setSettingsOpen(false)}
                className="flex-1 py-2 border border-slate-300 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="flex-1 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50"
              >
                {savingSettings ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}